import React, { useState, useEffect } from 'react'
import { useTheme, alpha } from '@mui/material'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import defaultTexts from '../../texts/texts'
import { isValidEmail } from '../_common/_helpers/validation'
import { coreTypes, openStackAdminTypes, iSenderTypes } from './_common/notificationTypes'
import CustomBackdrop from '../_common/CustomBackdrop'
import CustomTextField from '../_common/_form_fields/CustomTextField'
import CustomCheckboxField from '../_common/_form_fields/CustomCheckboxField'

const NotificationEmailAdd = (props) => {
  const { group, emails, setFetchEmails, emailRequest, handleAddEmailDialogClose } = props
  const theme = useTheme()

  const [email, setEmail] = useState('')
  const [types, setTypes] = useState([])
  const [typeList, setTypeList] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState({})

  const fieldBoxStyle = {
    padding: '12px 0px'
  }

  const formControlStyle = {
    background: theme.palette.aliceBlue,
    color: theme.palette.vodka,
    borderRadius: '5px',
    width: '100%',
  }

  const outlinedInputStyle = {
    '& .MuiInputBase-input::label': {
      color: theme.palette.vodka,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.vodka,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.vodka,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.vodka,
    },
  }

  const checkboxStyle = {
    color: theme.palette.violetsAreBlue,

    '&.Mui-checked': {
      color: theme.palette.violetsAreBlue,
    },

    '& .MuiSvgIcon-root': {
      fontSize: 24,
    },
  }

  const checkboxLabelStyle = {
    color: theme.palette.oxfordBlue,
    fontSize: '16px',
  }

  const errorHelperStyle = {
    color: theme.palette.coralRed,
    paddingLeft: '5px',
  }

  const saveButtonStyle = {
    fontWeight: '500',
    color: 'white',
    background: theme.palette.greenSheen,
    fontSize: '18px',
    height: '40px',
    borderRadius: '20px',
    padding: '0px 30px',
    marginTop: '20px',
    textTransform: 'capitalize',

    '&:hover': {
      background: alpha(theme.palette.greenSheen, 0.8),
    },
  }

  const formLabelStyle = {
    color: theme.palette.grayX11,
    fontSize: '16px',
    fontWeight: '500',
    paddingTop: '20px',
  }

  const formLabelHelperStyle = {
    color: theme.palette.coralRed,
    fontSize: '12px',
  }

  const handleChangeEmail = (e) => {
    const value = e.target.value
    setEmail(value)
    if (isValidEmail(value)) {
      setErrors(prev => { return { ...prev, email: null } })
    }
  }

  const handleChangeTypes = (value, field_key) => {
    if (value) {
      setTypes(prev => [...prev, field_key])
    } else {
      setTypes(prev => prev.filter(item => item !== field_key))
    }
  }

  const getEmailList = (type) => {
    const email_object = emails.find(item => item.group === group && item.type === type)
    return email_object ? email_object.emails : []
  }

  const getRecordId = (type) => {
    const email_object = emails.find(item => item.group === group && item.type === type)
    return email_object ? email_object.id : null
  }

  const handleClickSubmit = async () => {
    setIsLoading(true)
    let _errors = {}

    if (!email) {
      _errors.email = defaultTexts.pleaseFillOut
    } else if (!isValidEmail(email)) {
      _errors.email = defaultTexts.invalidEmail
    }

    if (types.length === 0) {
      _errors.types = defaultTexts.notificationTypesError
    }

    if (Object.keys(_errors).length === 0) {
      for (let index = 0; index < types.length; index++) {
        const item = types[index]

        const currentEmailList = getEmailList(item)
        const record_id = getRecordId(item)

        if (currentEmailList.length > 0) {
          const postData = {
            emails: [...currentEmailList, email], 
          }
  
          await emailRequest({ method: 'PUT', data: postData, record_id })
        } else {
          const postData = {
            group,
            type: item,
            emails: [...currentEmailList, email], 
          }
  
          await emailRequest({ method: 'POST', data: postData })
        }

        setFetchEmails(prev => prev + 1)
        handleAddEmailDialogClose()
      }
    }

    setErrors(_errors)
    setIsLoading(false)
  }

  useEffect(() => {
    let type_list = coreTypes

    if (group === 'openstack_admin') {
      type_list = openStackAdminTypes
    }

    if (group === 'isender') {
      type_list = iSenderTypes
    }

    setTypeList(type_list)
  }, [group])

  useEffect(() => {
    let _types = []
    typeList.forEach(item => _types.push(item.value))
    setTypes(_types)
  }, [typeList])

  return (
    <>
      { isLoading && <CustomBackdrop open={isLoading} /> }

      <Box sx={fieldBoxStyle}>
        <CustomTextField 
          variant='outlined'
          label={defaultTexts.email}
          currentValue={email}
          setCurrentValue={handleChangeEmail}
          required={true}
          error={errors.email ? true : false}
          loginHelperText={errors.email}
          loginHelperSx={errorHelperStyle}
          size='small'
          sx={formControlStyle}
          outlinedInputSx={outlinedInputStyle}
        />
      </Box>

      <Box sx={formLabelStyle}>{defaultTexts.notificationTypes}</Box>
      {
        (errors.types && types.length === 0) &&
        <Box sx={formLabelHelperStyle}>{defaultTexts.notificationTypesError}</Box>
      }

      {
        typeList.map((item, index) => (
          <Box key={index}>
            <CustomCheckboxField
              label={item.keyword}
              checked={types.includes(item.value)}
              handleChange={handleChangeTypes}
              field_key={item.value}
              sx={checkboxStyle}
              sxLabel={checkboxLabelStyle}
            />
          </Box>
        ))
      }

      <Stack
        direction='row'
        justifyContent='flex-end'
      >
        <Button
          variant='contained'
          disableRipple
          disableElevation
          disabled={false}
          onClick={handleClickSubmit}
          sx={saveButtonStyle}
        >
          {defaultTexts.save}
        </Button>
      </Stack>
    </>
  )
}

export default NotificationEmailAdd